import React, { memo, useCallback, useMemo } from "react";
import { FaPlus, FaMinus } from "react-icons/fa";
import { MenuItem } from "../redux/slices/menuItemsSlice";
import { formatPrice } from "../utils/priceUtils";
import { getCartQuantity, getCartItemId } from "../utils/cartUtils";

interface ClassicMenuItemCardProps {
  item: MenuItem;
  onItemClick?: (item: MenuItem) => void;
  onAddToCart?: (item: MenuItem) => void;
  showAddToCart?: boolean;
  cartItems?: any[];
  onUpdateQuantity?: (cartItemId: string, quantity: number) => void;
  onIncrementQuantity?: (item: MenuItem) => void;
  onDecrementQuantity?: (itemId: string) => void;
}

// Subcomponent: Menu Item Image
const MenuItemImage: React.FC<{
  item: MenuItem;
  onImageError: (e: React.SyntheticEvent<HTMLImageElement>) => void;
}> = ({ item, onImageError }) => (
  <div className="relative aspect-square sm:aspect-[4/3] overflow-hidden bg-gradient-to-br from-amber-50 to-orange-50">
    {item.image ? (
      <img
        src={item.image}
        alt={`${item.name} - Delicious classic menu item available for order`}
        className="w-full h-full object-cover transition-transform duration-300 hover:scale-105 rounded-b-2xl"
        onError={onImageError}
        loading="lazy"
      />
    ) : (
      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-amber-100 to-orange-100">
        <span className="text-4xl sm:text-6xl font-bold uppercase text-amber-600 font-serif drop-shadow-sm">
          {item.name.charAt(0)}
        </span>
      </div>
    )}


    {/* Decorative border */}
    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 via-yellow-500 to-amber-400"></div>
  </div>
);

// Subcomponent: Menu Item Info
const MenuItemInfo: React.FC<{ item: MenuItem }> = ({ item }) => (
  <div className="mb-3">
    <h3 className="text-lg sm:text-xl font-bold text-gray-800 line-clamp-1 font-serif">
      {item.name}
    </h3>
  </div>
);

// Subcomponent: Menu Item Actions
const MenuItemActions: React.FC<{
  item: MenuItem;
  isOutOfStock: boolean;
  showAddToCart: boolean;
  cartQuantity: number;
  cartItemId: string;
  onAddToCart: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onUpdateQuantity?: (cartItemId: string, quantity: number) => void;
  onIncrementQuantity?: (item: MenuItem) => void;
  onDecrementQuantity?: (itemId: string) => void;
}> = ({ item, isOutOfStock, showAddToCart, cartQuantity, cartItemId, onAddToCart, onUpdateQuantity, onIncrementQuantity, onDecrementQuantity }) => {
  // Extract price from object or use as-is
  const displayPrice = typeof item.price === 'object' ? item.price.online : item.price;
  
  return (
    <div className="flex items-center justify-between">
      <span className="text-xl sm:text-2xl font-bold text-amber-600 font-serif drop-shadow-sm">
        {formatPrice(displayPrice)}
      </span>

      {showAddToCart && (
      cartQuantity > 0 ? (
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (onDecrementQuantity) {
                onDecrementQuantity(item.pk_id.toString());
              } else if (onUpdateQuantity) {
                onUpdateQuantity(cartItemId, cartQuantity - 1);
              }
            }}
            className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center transition-all"
            aria-label="Decrease quantity"
          >
            <FaMinus className="w-3 h-3 text-gray-700" />
          </button>
          <span className="font-bold text-lg text-gray-900 min-w-[2rem] text-center">{cartQuantity}</span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (onIncrementQuantity) {
                onIncrementQuantity(item);
              } else if (onUpdateQuantity) {
                onUpdateQuantity(cartItemId, cartQuantity + 1);
              }
            }}
            className="w-8 h-8 rounded-full bg-amber-500 hover:bg-amber-600 text-white flex items-center justify-center transition-all"
            aria-label="Increase quantity"
          >
            <FaPlus className="w-3 h-3" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          disabled={isOutOfStock}
          aria-disabled={isOutOfStock}
          className={`px-4 sm:px-6 py-2 sm:py-2.5 font-semibold rounded-lg border-2 transition-all duration-300 font-serif ${
            isOutOfStock
              ? 'bg-gray-400 text-gray-600 border-gray-400 cursor-not-allowed'
              : 'bg-gradient-to-r from-amber-500 to-yellow-600 text-white border-amber-500 hover:from-amber-600 hover:to-yellow-700 hover:border-amber-600 shadow-md hover:shadow-lg'
          }`}
          onClick={onAddToCart}
          aria-label={`Add ${item.name} to cart${isOutOfStock ? ' - Out of Stock' : ''}`}
          data-testid={`add-to-cart-${item.id || item.pk_id}`}
        >
          {isOutOfStock ? 'Out of Stock' : 'Add'}
        </button>
      )
    )}
    </div>
  );
};

const ClassicMenuItemCard: React.FC<ClassicMenuItemCardProps> = ({
  item,
  onItemClick,
  onAddToCart,
  showAddToCart = true,
  cartItems = [],
  onUpdateQuantity,
  onIncrementQuantity,
  onDecrementQuantity,
}) => {
  // Early exit - only hide if disabled or not verified, but show out-of-stock items
  if (!item.is_enabled || !item.is_item_verified) {
    return null;
  }

  // Check if item is out of stock
  const isOutOfStock = !item.inventoryStatus;

  // Get cart quantity for this item
  const cartQuantity = useMemo(() => 
    getCartQuantity(item.pk_id.toString(), cartItems),
    [item.pk_id, cartItems]
  );

  const cartItemId = useMemo(() => 
    getCartItemId(item.pk_id.toString()),
    [item.pk_id]
  );

  const handleImageError = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      (e.target as HTMLImageElement).src = `https://via.placeholder.com/300x200/f8f9fa/6c757d?text=${encodeURIComponent(
        item.name
      )}`;
    },
    [item.name]
  );

  const handleCardClick = useCallback(() => {
    onItemClick?.(item);
  }, [onItemClick, item]);

  const handleAddToCart = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      if (!isOutOfStock) {
        onAddToCart?.(item);
      }
    },
    [onAddToCart, item, isOutOfStock]
  );

  // Extract price for aria-label
  const displayPrice = typeof item.price === 'object' ? item.price.online : item.price;

  return (
    <div
      className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-300 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer overflow-hidden hover:-translate-y-1"
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      aria-label={`View details for ${item.name}, priced at ${formatPrice(
        displayPrice
      )}${isOutOfStock ? ' - Out of Stock' : ''}`}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && handleCardClick()}
      data-testid={`menu-item-card-${item.id || item.pk_id}`}
    >
      <MenuItemImage 
        item={item} 
        onImageError={handleImageError} 
      />
      
      {/* Content */}
      <div className="p-4 sm:p-5">
        <MenuItemInfo item={item} />

        {/* Description */}
        {item.description && (
          <p className="text-gray-600 text-sm sm:text-base leading-relaxed mb-4 line-clamp-2 font-serif">
            {item.description}
          </p>
        )}

        <MenuItemActions 
          item={item}
          isOutOfStock={isOutOfStock}
          showAddToCart={showAddToCart}
          cartQuantity={cartQuantity}
          cartItemId={cartItemId}
          onAddToCart={handleAddToCart}
          onUpdateQuantity={onUpdateQuantity}
          onIncrementQuantity={onIncrementQuantity}
          onDecrementQuantity={onDecrementQuantity}
        />
      </div>
    </div>
  );
};

// Optimized memoization - only check critical props that affect rendering
const areEqual = (prev: ClassicMenuItemCardProps, next: ClassicMenuItemCardProps) => {
  return (
    // Core item identity and critical display props
    (prev.item.id || prev.item.pk_id) === (next.item.id || next.item.pk_id) &&
    prev.item.price === next.item.price &&
    prev.item.is_enabled === next.item.is_enabled &&
    prev.item.inventoryStatus === next.item.inventoryStatus &&
    prev.item.is_item_verified === next.item.is_item_verified &&
    // Component behavior props
    prev.showAddToCart === next.showAddToCart &&
    prev.onItemClick === next.onItemClick &&
    prev.onAddToCart === next.onAddToCart &&
    prev.cartItems === next.cartItems
  );
};

export default memo(ClassicMenuItemCard, areEqual);
